/**
 * usePurchaseOrder hooks — Issue #331.
 *
 * Typed hooks for purchase order list/detail/create/update/state-transition/
 * receive endpoints, built on the generated @jinzhuan/api-client types.
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type PurchaseOrderSummaryDto =
  ApiComponents['schemas']['PurchaseOrderSummaryDto'];
export type PurchaseOrderDetailDto =
  ApiComponents['schemas']['PurchaseOrderDetailDto'];
export type PurchaseOrderListResponseDto =
  ApiComponents['schemas']['PurchaseOrderListResponseDto'];
export type PurchaseOrderItemDto =
  ApiComponents['schemas']['PurchaseOrderItemDto'];
export type CreatePurchaseOrderDto =
  ApiComponents['schemas']['CreatePurchaseOrderDto'];
export type CreatePurchaseOrderItemDto =
  ApiComponents['schemas']['CreatePurchaseOrderItemDto'];
export type UpdatePurchaseOrderDto =
  ApiComponents['schemas']['UpdatePurchaseOrderDto'];
export type PurchaseOrderActionDto =
  ApiComponents['schemas']['PurchaseOrderActionDto'];
export type CancelPurchaseOrderDto =
  ApiComponents['schemas']['CancelPurchaseOrderDto'];
export type ReceivePurchaseOrderDto =
  ApiComponents['schemas']['ReceivePurchaseOrderDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (effective === 409) {
    return { message: '数据已被其他用户修改，请刷新后重试', code: 'CONFLICT' };
  }
  if (effective === 403) {
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export function usePurchaseOrderList() {
  const [data, setData] = useState<PurchaseOrderListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      status?: string;
      supplierId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter?.status) query.status = filter.status;
        if (filter?.supplierId) query.supplierId = filter.supplierId;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/purchase-orders',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function usePurchaseOrderDetail() {
  const [data, setData] = useState<PurchaseOrderDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/purchase-orders/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
}

export function usePurchaseOrderActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreatePurchaseOrderDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/purchase-orders',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, dto: UpdatePurchaseOrderDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/purchase-orders/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  function makeActionCallback<T extends PurchaseOrderActionDto>(
    path:
      | '/api/v1/purchase-orders/{id}/submit'
      | '/api/v1/purchase-orders/{id}/approve'
      | '/api/v1/purchase-orders/{id}/return-to-draft'
      | '/api/v1/purchase-orders/{id}/close',
  ) {
    return async (id: string, dto: T) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(path, {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    };
  }

  const submit = useCallback(
    makeActionCallback('/api/v1/purchase-orders/{id}/submit'),
    [],
  );
  const approve = useCallback(
    makeActionCallback('/api/v1/purchase-orders/{id}/approve'),
    [],
  );
  const returnToDraft = useCallback(
    makeActionCallback('/api/v1/purchase-orders/{id}/return-to-draft'),
    [],
  );
  const close = useCallback(
    makeActionCallback('/api/v1/purchase-orders/{id}/close'),
    [],
  );

  const cancel = useCallback(
    async (id: string, dto: CancelPurchaseOrderDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/purchase-orders/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const receive = useCallback(
    async (id: string, dto: ReceivePurchaseOrderDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/purchase-orders/{id}/receive',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    create,
    update,
    submit,
    approve,
    returnToDraft,
    close,
    cancel,
    receive,
  };
}
